import { Check, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectionCheckboxBoxProps {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  /**
   * Slightly larger box for the sticky list header's "select all" control so
   * it reads as the master toggle above the per-row boxes.
   */
  size?: "sm" | "md";
}

/**
 * Presentational multi-select checkbox for the recap list (rows + header
 * "select all"). Purely visual — the surrounding element owns the hitbox,
 * click handling (incl. shift-range) and focus, so this can be dropped into a
 * `<button>` without nesting interactive elements.
 *
 * Elegant flat style: soft rounded square, gray hairline border when empty,
 * solid brand-blue fill with a crisp check when ticked, and a dash when the
 * selection is partial. `group-hover/checkbox` (set by the parent hitbox)
 * warms the border on hover so the affordance is obvious before clicking.
 */
export function SelectionCheckboxBox({
  checked,
  indeterminate = false,
  disabled = false,
  size = "sm",
}: SelectionCheckboxBoxProps) {
  const active = checked || indeterminate;
  const dim = size === "md" ? "h-[19px] w-[19px]" : "h-[17px] w-[17px]";
  const icon = size === "md" ? "h-3.5 w-3.5" : "h-3 w-3";
  return (
    <span
      aria-hidden
      className={cn(
        "flex items-center justify-center rounded-[5px] border transition-[background-color,border-color,box-shadow,transform] duration-150",
        dim,
        active
          ? "border-blue-600 bg-blue-600 text-white shadow-sm"
          : "border-gray-300 bg-white text-transparent shadow-[inset_0_0_0_1px_rgba(255,255,255,0.6)] group-hover/checkbox:border-blue-400 group-hover/checkbox:bg-blue-50/40",
        !disabled &&
          "group-active/checkbox:scale-90 group-focus-visible/checkbox:ring-2 group-focus-visible/checkbox:ring-blue-500/40 group-focus-visible/checkbox:ring-offset-1",
        disabled && "opacity-50",
      )}
    >
      {indeterminate ? (
        <Minus className={icon} strokeWidth={3.5} />
      ) : (
        <Check
          className={cn(
            "transition-opacity duration-150",
            icon,
            checked ? "opacity-100" : "opacity-0",
          )}
          strokeWidth={3.5}
        />
      )}
    </span>
  );
}
